import { isMacAppStore } from '@/lib/appDistribution';
import { AppState, Linking, Platform, Settings } from 'react-native';
import { accountApi } from '@/lib/api/account';
import { creditPacksApi } from '@/lib/api/creditPacks';
import { fetchWithAuth } from '@/lib/api/client';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { trackWhopPurchase } from '@/constants/utils/analytics';

const POLL_INTERVAL_MS = 15000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;
const watchers = new Map();
const storageKey = (userId) => `gab-btcpay-pending:${userId}`;
const ownerId = () => useAuthStore.getState().user?.id;
const notify = (message, type = 'info') => useToastStore.getState().actions.addToast(message, type);

function unwrapPayload(response) {
  return response?.data && typeof response.data === 'object'
    ? { ...response, ...response.data } : response || {};
}

function readPending(userId) {
  if (userId && Platform.OS === 'macos') {
    try {
      const value = JSON.parse(Settings.get(storageKey(userId)) || '[]');
      return Array.isArray(value) ? value.filter(item => typeof item?.invoiceId === 'string'
        && ['credits', 'plus'].includes(item.kind)) : [];
    } catch { return []; }
  }
  if (!userId || Platform.OS !== 'web' || typeof window === 'undefined') return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey(userId)) || '[]');
    return Array.isArray(value) ? value.filter(item => typeof item.invoiceId === 'string'
      && ['credits', 'plus'].includes(item.kind)) : [];
  } catch { return []; }
}

function persist(context, remove = false) {
  if (context.userId && Platform.OS === 'macos') {
    const other = readPending(context.userId).filter(item => item.invoiceId !== context.invoiceId || item.kind !== context.kind);
    Settings.set({ [storageKey(context.userId)]: JSON.stringify(remove ? other : [...other, context]) });
    return;
  }
  if (!context.userId || Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const other = readPending(context.userId).filter(item => item.invoiceId !== context.invoiceId || item.kind !== context.kind);
    window.localStorage.setItem(storageKey(context.userId), JSON.stringify(remove ? other : [...other, context]));
  } catch { notify('Your browser could not save payment recovery details. Keep this page open until payment is confirmed.'); }
}

export function isBtcPayStatusFulfilled(payload) {
  return !payload?.error && ['active', 'completed'].includes(String(payload?.purchaseStatus || '').toLowerCase());
}

function invoiceStateOf(payload) {
  return String(payload?.status || payload?.invoiceStatus || '').toLowerCase();
}

function purchaseStateOf(payload) {
  return String(payload?.purchaseStatus || '').toLowerCase();
}

function additionalStateOf(payload) {
  return String(payload?.additionalStatus || '').toLowerCase();
}

export function isBtcPayAwaitingPayment(payload) {
  if (!payload || payload.expiredUnpaid) return false;
  if (payload.awaitingPayment) return true;
  const invoiceState = invoiceStateOf(payload);
  const additional = additionalStateOf(payload);
  const purchaseStatus = purchaseStateOf(payload);
  return ['new', 'processing', 'paid'].includes(invoiceState)
    || ['paidlate', 'paidpartial', 'paidover'].includes(additional)
    || purchaseStatus === 'processing';
}

export function isBtcPayExpiredUnpaid(payload) {
  if (!payload || payload.awaitingPayment || isBtcPayAwaitingPayment(payload)) return false;
  if (payload.expiredUnpaid || payload.reason === 'expired_unpaid') return true;
  const invoiceState = invoiceStateOf(payload);
  const purchaseStatus = purchaseStateOf(payload);
  return invoiceState === 'expired' || purchaseStatus === 'expired';
}

export function classifyBtcPayWatchState(payload) {
  if (isBtcPayStatusFulfilled(payload)) return 'fulfilled';
  if (isBtcPayAwaitingPayment(payload)) return 'awaiting';
  if (isBtcPayExpiredUnpaid(payload)) return 'expired_unpaid';
  const purchaseStatus = purchaseStateOf(payload);
  const invoiceState = invoiceStateOf(payload);
  if (['failed', 'invalid', 'canceled', 'cancelled'].includes(purchaseStatus)
    || invoiceState === 'invalid') {
    return 'failed';
  }
  return 'pending';
}

export function btcPayCheckoutOpenError(response) {
  const payload = unwrapPayload(response);
  if (payload.pending || payload.awaitingPayment) {
    return {
      type: 'info',
      message: payload.message || 'Bitcoin payment is still confirming. Do not pay again.',
    };
  }
  return { type: 'error', message: payload.message || 'Failed to create Bitcoin invoice' };
}

export function validateBtcPayCheckoutUrl(checkoutUrl, checkoutOrigin) {
  const url = new URL(checkoutUrl);
  const allowed = new URL(checkoutOrigin);
  if (url.protocol !== 'https:' || allowed.protocol !== 'https:' || url.username || url.password
    || allowed.username || allowed.password || url.origin !== allowed.origin) {
    throw new Error('Bitcoin checkout returned an untrusted payment URL.');
  }
  return url.href;
}

export function watchBtcPayInvoice({ invoiceId, kind = 'plus', onSettled, onFailed, onTimeout, userId = ownerId() } = {}) {
  if (!invoiceId || !userId) return () => {};
  const key = `${userId}:${kind}:${invoiceId}`;
  const existing = watchers.get(key);
  if (existing) {
    if (onSettled) existing.onSettled = onSettled;
    if (onFailed) existing.onFailed = onFailed;
    if (onTimeout) existing.onTimeout = onTimeout;
    return existing.cancel;
  }
  const context = { invoiceId, kind, userId };
  let timer;
  let cancelled = false;
  const startedAt = Date.now();
  const entry = { onSettled, onFailed, onTimeout, cancel: () => {
    cancelled = true;
    clearTimeout(timer);
    watchers.delete(key);
  } };
  watchers.set(key, entry);
  persist(context);
  const poll = async () => {
    if (cancelled) return;
    if (ownerId() !== userId) { entry.cancel(); return; }
    if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
      entry.cancel();
      const message = 'Bitcoin payment is not confirmed yet. Your invoice is saved; return to the app to check again. Do not pay again if you already sent Bitcoin.';
      if (entry.onTimeout) entry.onTimeout(context); else notify(message);
      return;
    }
    try {
      const raw = kind === 'credits'
        ? await creditPacksApi.checkBtcPayInvoiceStatus(invoiceId)
        : await accountApi.checkBtcPayInvoiceStatus(invoiceId);
      if (cancelled) return;
      if (ownerId() !== userId) { entry.cancel(); return; }
      const payload = unwrapPayload(raw);
      const watchState = classifyBtcPayWatchState(payload);
      if (watchState === 'fulfilled') {
        if (Platform.OS === 'macos') {
          const { refreshMacBilling, rememberMacCheckout } = require('@/lib/macCheckout');
          // Keep recovery running if this refresh fails or an older in-flight
          // account request lands afterwards. Browser launch is not fulfillment.
          rememberMacCheckout(userId);
          await refreshMacBilling(userId, () => !cancelled);
          if (cancelled) return;
          if (ownerId() !== userId) { entry.cancel(); return; }
        }
        entry.cancel();
        persist(context, true);
        trackWhopPurchase({ id: invoiceId, value: payload.amount, currency: payload.currency || 'USD' });
        if (entry.onSettled) entry.onSettled(payload);
        else {
          if (Platform.OS !== 'macos') {
            useAuthStore.getState().fetchUser?.();
            useAuthStore.getState().fetchUserCredits?.({ force: true });
          }
          notify(kind === 'credits' ? 'Bitcoin payment confirmed. Your credits are available.' : 'Bitcoin payment confirmed. Your subscription is active.', 'success');
        }
        return;
      }
      if (watchState === 'expired_unpaid') {
        entry.cancel();
        persist(context, true);
        if (entry.onFailed) entry.onFailed(payload);
        return;
      }
      if (watchState === 'failed') {
        entry.cancel();
        persist(context, true);
        if (entry.onFailed) entry.onFailed(payload);
        else notify('Bitcoin checkout expired or could not complete. If you sent payment, do not pay again; return to check its status or contact support.', 'error');
        return;
      }
    } catch {
      // Network/provider errors are retried within a bounded window, then surfaced.
    }
    if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
  };
  timer = setTimeout(poll, 0);
  return entry.cancel;
}

export function resumeBtcPayCheckouts(userId = ownerId()) {
  if (isMacAppStore()) return () => {};
  const resume = () => readPending(userId).forEach(context => watchBtcPayInvoice({ ...context, userId }));
  resume();
  if (Platform.OS === 'web' && typeof window !== 'undefined') window.addEventListener('focus', resume);
  const subscription = Platform.OS === 'macos'
    ? AppState.addEventListener('change', state => { if (state === 'active') resume(); }) : null;
  return () => {
    subscription?.remove();
    if (Platform.OS === 'web' && typeof window !== 'undefined') window.removeEventListener('focus', resume);
    for (const [key, entry] of watchers) if (key.startsWith(`${userId}:`)) entry.cancel();
  };
}

export async function openBtcPayCheckout({ checkoutUrl, invoiceId, kind = 'plus', onSettled, onFailed, onTimeout, expectedUserId } = {}) {
  if (isMacAppStore()) throw new Error('Purchases in this edition use the App Store.');
  if (!checkoutUrl || !invoiceId) throw new Error('Bitcoin checkout is missing invoice details.');
  const userId = ownerId();
  if (!userId) throw new Error('Please sign in before opening Bitcoin checkout.');
  if (Platform.OS === 'macos' && expectedUserId !== undefined && expectedUserId !== userId) {
    throw new Error('Your account changed. Please reopen Bitcoin checkout.');
  }
  const config = unwrapPayload(await fetchWithAuth('/json/payments/btcpay-config'));
  const trustedUrl = validateBtcPayCheckoutUrl(checkoutUrl, config.checkoutOrigin);
  if (Platform.OS === 'macos' && ownerId() !== userId) {
    throw new Error('Your account changed. Please reopen Bitcoin checkout.');
  }
  // Save before navigation so reloads, closed tabs and returning from checkout can recover.
  persist({ invoiceId, kind, userId });
  await Linking.openURL(trustedUrl);
  watchBtcPayInvoice({ invoiceId, kind, onSettled, onFailed, onTimeout, userId });
  return true;
}
